/**
 * UserResourcesModal Component
 * Modal dialog for viewing and managing user resources (artifacts, library, temp)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, FolderOpen, Archive, Image, FileStack, Trash2, AlertCircle, ExternalLink, Download, ChevronDown, ChevronRight, File, Music, Video, FileText, Folder } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/common';
import { useI18n, type ResourcesMessages } from '@/i18n';
import {
  useUserResourceSummary,
  useUserArtifacts,
  useUserLibrary,
  useUserTemp,
  useDeleteResource,
  useDeleteAllUserResources,
} from '../hooks/useResources';
import { useArtifactTypes } from '@/features/system-settings/hooks/useSystemConfig';
import { formatFileSize } from '@/types/resources';
import type { ResourceInfo, ResourceType, ArtifactFieldValue } from '@/types/resources';
import type { ArtifactType } from '@/types/system';

type TabType = 'artifacts' | 'library' | 'temp';

// Map TabType to translation key
const tabToTypeKey: Record<TabType, 'artifact' | 'library' | 'temp'> = {
  artifacts: 'artifact',
  library: 'library',
  temp: 'temp',
};

// Helper to get icon for media content type
const getMediaIcon = (contentType?: string) => {
  if (!contentType) return <File className="w-4 h-4" />;
  if (contentType.startsWith('image/')) return <Image className="w-4 h-4" />;
  if (contentType.startsWith('audio/')) return <Music className="w-4 h-4" />;
  if (contentType.startsWith('video/')) return <Video className="w-4 h-4" />;
  if (contentType.includes('pdf') || contentType.includes('document')) return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

// Field item component for artifact fields
interface FieldItemProps {
  fieldKey: string;
  field: ArtifactFieldValue;
}

const FieldItem: React.FC<FieldItemProps> = ({ fieldKey, field }) => {
  const isImage = field.content_type?.startsWith('image/');

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded hover:bg-gray-100">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Field Icon */}
        <div className={`w-8 h-8 rounded flex items-center justify-center ${
          field.is_media ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
        }`}>
          {field.is_media ? getMediaIcon(field.content_type) : <FileText className="w-4 h-4" />}
        </div>

        {/* Field Info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-700">{fieldKey}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {field.is_media ? (
              <>
                <span>{field.content_type || 'media'}</span>
                {field.value && <span>· {String(field.value)}</span>}
              </>
            ) : (
              <span className="truncate max-w-xs" title={String(field.value)}>
                {String(field.value || '-')}
              </span>
            )}
          </div>
        </div>

        {/* Image preview */}
        {isImage && field.url && (
          <div className="w-12 h-12 rounded overflow-hidden bg-gray-200 flex-shrink-0">
            <img
              src={field.url}
              alt={fieldKey}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      {field.is_media && field.url && (
        <div className="flex items-center gap-1 ml-2">
          <a
            href={field.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-gray-400 hover:text-blue-600"
            title="打开"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a
            href={field.url}
            download={String(field.value || fieldKey)}
            className="p-1.5 text-gray-400 hover:text-green-600"
            title="下载"
          >
            <Download className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
};

// Artifact item component with expandable fields
interface ArtifactItemProps {
  resource: ResourceInfo;
  t: ResourcesMessages;
  deleteConfirm: string | null;
  setDeleteConfirm: (id: string | null) => void;
  handleDeleteResource: (id: string) => void;
  deleteResourceMutation: { isPending: boolean };
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ArtifactItem: React.FC<ArtifactItemProps> = ({
  resource,
  t,
  deleteConfirm,
  setDeleteConfirm,
  handleDeleteResource,
  deleteResourceMutation,
  isExpanded,
  onToggleExpand,
}) => {
  const hasFields = resource.fields && Object.keys(resource.fields).length > 0;
  const mediaFieldCount = hasFields
    ? Object.values(resource.fields!).filter(f => f.is_media).length
    : 0;

  return (
    <div className={`border rounded-lg overflow-hidden ${resource.is_expired ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
      {/* Header - clickable to expand */}
      <div
        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${
          resource.is_expired ? 'bg-red-50' : 'bg-white'
        }`}
        onClick={hasFields ? onToggleExpand : undefined}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Expand icon */}
          {hasFields ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-4" />
          )}

          {/* Folder/File Icon */}
          <div className="w-10 h-10 rounded flex items-center justify-center bg-blue-100 text-blue-600">
            {hasFields ? <Folder className="w-5 h-5" /> : <Archive className="w-5 h-5" />}
          </div>

          {/* Artifact Info */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {resource.name}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              {resource.version && (
                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                  v{resource.version}
                </span>
              )}
              {hasFields && (
                <span>{mediaFieldCount} 个媒体文件</span>
              )}
              <span>·</span>
              <span>{new Date(resource.created_at).toLocaleDateString()}</span>
              {resource.is_expired && (
                <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                  {t.status.expired}
                </span>
              )}
              {resource.is_current && (
                <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-600">
                  {t.status.current}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4" onClick={e => e.stopPropagation()}>
          {deleteConfirm === resource.id ? (
            <div className="flex items-center gap-1">
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteResource(resource.id)}
                disabled={deleteResourceMutation.isPending}
              >
                {t.actions.confirmDelete}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirm(null)}
              >
                {t.actions.cancel}
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirm(resource.id)}
              className="p-2 text-gray-400 hover:text-red-600"
              title={t.actions.delete}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded fields */}
      {isExpanded && hasFields && (
        <div className="border-t border-gray-100 p-3 space-y-2 bg-gray-50/50">
          {Object.entries(resource.fields!).map(([key, field]) => (
            <FieldItem key={key} fieldKey={key} field={field} />
          ))}
        </div>
      )}
    </div>
  );
};

interface ResourceItemProps {
  resource: ResourceInfo;
  t: ResourcesMessages;
  deleteConfirm: string | null;
  setDeleteConfirm: (id: string | null) => void;
  handleDeleteResource: (id: string) => void;
  deleteResourceMutation: { isPending: boolean };
  showSubtype?: boolean;
}

const ResourceItem: React.FC<ResourceItemProps> = ({
  resource,
  t,
  deleteConfirm,
  setDeleteConfirm,
  handleDeleteResource,
  deleteResourceMutation,
  showSubtype = true,
}) => {
  return (
    <div
      className={`flex items-center justify-between p-3 ${
        resource.is_expired
          ? 'bg-red-50'
          : 'bg-white hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* File Type Icon */}
        <div className={`w-10 h-10 rounded flex items-center justify-center ${
          resource.resource_type === 'artifact'
            ? 'bg-blue-100 text-blue-600'
            : resource.resource_type === 'library'
            ? 'bg-green-100 text-green-600'
            : 'bg-yellow-100 text-yellow-600'
        }`}>
          {resource.resource_type === 'artifact' ? (
            <Archive className="w-5 h-5" />
          ) : resource.resource_type === 'library' ? (
            <Image className="w-5 h-5" />
          ) : (
            <FileStack className="w-5 h-5" />
          )}
        </div>

        {/* File Info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {resource.name}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <span>{resource.content_type}</span>
            <span>·</span>
            <span>{formatFileSize(resource.size)}</span>
            <span>·</span>
            <span>{new Date(resource.created_at).toLocaleDateString()}</span>
            {showSubtype && resource.subtype && (
              <>
                <span>·</span>
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  {resource.subtype}
                </span>
              </>
            )}
            {resource.is_expired && (
              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                {t.status.expired}
              </span>
            )}
            {resource.is_current && (
              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-600">
                {t.status.current}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 ml-4">
        {resource.url && (
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-blue-600"
            title={t.actions.open}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {resource.url && (
          <a
            href={resource.url}
            download={resource.name}
            className="p-2 text-gray-400 hover:text-green-600"
            title={t.actions.download}
          >
            <Download className="w-4 h-4" />
          </a>
        )}
        {deleteConfirm === resource.id ? (
          <div className="flex items-center gap-1">
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDeleteResource(resource.id)}
              disabled={deleteResourceMutation.isPending}
            >
              {t.actions.confirmDelete}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteConfirm(null)}
            >
              {t.actions.cancel}
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setDeleteConfirm(resource.id)}
            className="p-2 text-gray-400 hover:text-red-600"
            title={t.actions.delete}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

interface UserResourcesModalProps {
  isOpen: boolean;
  userId: string | null;
  userName?: string;
  onClose: () => void;
}

export const UserResourcesModal: React.FC<UserResourcesModalProps> = ({
  isOpen,
  userId,
  userName,
  onClose,
}) => {
  const { messages } = useI18n();
  const t = messages.resources;
  const [activeTab, setActiveTab] = useState<TabType>('artifacts');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState<ResourceType | 'all' | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());

  // Fetch summary and resources
  const { data: summary, isLoading: summaryLoading } = useUserResourceSummary(userId);
  const { data: artifactsData, isLoading: artifactsLoading } = useUserArtifacts(
    activeTab === 'artifacts' ? userId : null,
    { include_urls: true, include_expired: true, limit: 100 }
  );
  const { data: libraryData, isLoading: libraryLoading } = useUserLibrary(
    activeTab === 'library' ? userId : null,
    { include_urls: true, limit: 100 }
  );
  const { data: tempData, isLoading: tempLoading } = useUserTemp(
    activeTab === 'temp' ? userId : null,
    { include_urls: true, limit: 100 }
  );

  // Fetch artifact types for grouping
  const { data: artifactTypes } = useArtifactTypes();

  // Group artifacts by subtype
  const groupedArtifacts = useMemo(() => {
    if (!artifactsData?.resources) return null;

    const groups: Record<string, ResourceInfo[]> = {};
    const uncategorized: ResourceInfo[] = [];

    artifactsData.resources.forEach((resource) => {
      if (resource.subtype) {
        if (!groups[resource.subtype]) {
          groups[resource.subtype] = [];
        }
        groups[resource.subtype].push(resource);
      } else {
        uncategorized.push(resource);
      }
    });

    // Add uncategorized if any
    if (uncategorized.length > 0) {
      groups['_uncategorized'] = uncategorized;
    }

    return groups;
  }, [artifactsData?.resources]);

  // Get artifact type display name
  const getArtifactTypeName = (typeId: string): string => {
    if (typeId === '_uncategorized') return t.groups?.uncategorized || '未分类';
    const artifactType = artifactTypes?.find((at: ArtifactType) => at.id === typeId);
    if (artifactType?.config) {
      return artifactType.config.name_zh || artifactType.config.name || typeId;
    }
    return typeId;
  };

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Toggle artifact expansion (for multi-field artifacts)
  const toggleArtifact = (artifactId: string) => {
    setExpandedArtifacts((prev) => {
      const next = new Set(prev);
      if (next.has(artifactId)) {
        next.delete(artifactId);
      } else {
        next.add(artifactId);
      }
      return next;
    });
  };

  // Expand all groups when data loads
  useEffect(() => {
    if (groupedArtifacts && expandedGroups.size === 0) {
      setExpandedGroups(new Set(Object.keys(groupedArtifacts)));
    }
  }, [groupedArtifacts]);

  // Mutations
  const deleteResourceMutation = useDeleteResource(userId || '');
  const deleteAllMutation = useDeleteAllUserResources(userId || '');

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset state when modal closes or user changes
  useEffect(() => {
    if (!isOpen || !userId) {
      setActiveTab('artifacts');
      setDeleteConfirm(null);
      setDeleteAllConfirm(null);
      setExpandedGroups(new Set());
      setExpandedArtifacts(new Set());
    }
  }, [isOpen, userId]);

  if (!isOpen || !userId) return null;

  const tabs: { key: TabType; label: string; icon: React.ReactNode; count: number }[] = [
    {
      key: 'artifacts',
      label: t.types.artifact,
      icon: <Archive className="w-4 h-4" />,
      count: summary?.artifacts.count || 0,
    },
    {
      key: 'library',
      label: t.types.library,
      icon: <Image className="w-4 h-4" />,
      count: summary?.library.count || 0,
    },
    {
      key: 'temp',
      label: t.types.temp,
      icon: <FileStack className="w-4 h-4" />,
      count: summary?.temp.count || 0,
    },
  ];

  const getCurrentResources = (): ResourceInfo[] => {
    switch (activeTab) {
      case 'artifacts':
        return artifactsData?.resources || [];
      case 'library':
        return libraryData?.resources || [];
      case 'temp':
        return tempData?.resources || [];
      default:
        return [];
    }
  };

  const isCurrentLoading = () => {
    switch (activeTab) {
      case 'artifacts':
        return artifactsLoading;
      case 'library':
        return libraryLoading;
      case 'temp':
        return tempLoading;
      default:
        return false;
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    try {
      await deleteResourceMutation.mutateAsync(resourceId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  const handleDeleteAll = async (type: ResourceType | 'all') => {
    try {
      await deleteAllMutation.mutateAsync({
        resource_type: type === 'all' ? undefined : type,
        dry_run: false,
      });
      setDeleteAllConfirm(null);
    } catch (error) {
      console.error('Failed to delete all resources:', error);
    }
  };

  const resources = getCurrentResources();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t.modal.title}
              </h2>
              <p className="text-sm text-gray-500">
                {userName || userId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary Stats */}
        {summaryLoading ? (
          <div className="p-4 border-b border-gray-200">
            <Loading message={messages.common.loading} />
          </div>
        ) : summary ? (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{summary.total_count}</p>
                <p className="text-xs text-gray-500">{t.summary.totalFiles}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{formatFileSize(summary.total_size_bytes)}</p>
                <p className="text-xs text-gray-500">{t.summary.totalSize}</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700">{summary.artifacts.count}</p>
                <p className="text-xs text-gray-500">{t.types.artifact}</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700">{summary.library.count + summary.temp.count}</p>
                <p className="text-xs text-gray-500">{t.types.library}/{t.types.temp}</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Resources List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isCurrentLoading() ? (
            <Loading message={messages.common.loading} />
          ) : resources.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{t.modal.noResources}</p>
            </div>
          ) : activeTab === 'artifacts' && groupedArtifacts ? (
            /* Grouped artifacts view */
            <div className="space-y-4">
              {Object.entries(groupedArtifacts).map(([groupId, groupResources]) => (
                <div key={groupId} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(groupId)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {expandedGroups.has(groupId) ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <Archive className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-900">
                        {getArtifactTypeName(groupId)}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
                        {groupResources.length}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(groupResources.reduce((acc, r) => acc + r.size, 0))}
                    </span>
                  </button>

                  {/* Group Content */}
                  {expandedGroups.has(groupId) && (
                    <div className="p-2 space-y-2">
                      {groupResources.map((resource) => (
                        <ArtifactItem
                          key={resource.id}
                          resource={resource}
                          t={t}
                          deleteConfirm={deleteConfirm}
                          setDeleteConfirm={setDeleteConfirm}
                          handleDeleteResource={handleDeleteResource}
                          deleteResourceMutation={deleteResourceMutation}
                          isExpanded={expandedArtifacts.has(resource.id)}
                          onToggleExpand={() => toggleArtifact(resource.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Flat list view for library/temp */
            <div className="space-y-2">
              {resources.map((resource) => (
                <ResourceItem
                  key={resource.id}
                  resource={resource}
                  t={t}
                  deleteConfirm={deleteConfirm}
                  setDeleteConfirm={setDeleteConfirm}
                  handleDeleteResource={handleDeleteResource}
                  deleteResourceMutation={deleteResourceMutation}
                  showSubtype={true}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer with Delete All Actions */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <AlertCircle className="w-4 h-4" />
            {t.modal.deleteWarning}
          </div>
          <div className="flex items-center gap-2">
            {deleteAllConfirm ? (
              <>
                <span className="text-sm text-red-600 mr-2">
                  {deleteAllConfirm === 'all'
                    ? t.deleteAll.confirmAll
                    : t.deleteAll.confirmType.replace('{type}', t.types[tabToTypeKey[deleteAllConfirm as TabType]])}
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteAll(deleteAllConfirm)}
                  disabled={deleteAllMutation.isPending}
                >
                  {deleteAllMutation.isPending ? t.deleting : t.actions.confirmDelete}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteAllConfirm(null)}
                >
                  {t.actions.cancel}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteAllConfirm(activeTab as ResourceType)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t.deleteAll.type.replace('{type}', t.types[tabToTypeKey[activeTab]])}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteAllConfirm('all')}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t.deleteAll.all}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
